import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus,
  ChevronRight,
  Calendar,
  Plane,
  Briefcase,
  PartyPopper,
  Trash2,
} from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Page, Main, Card, Button, Badge, LoadingState, ErrorState, EmptyState, Modal } from '@/components/ui';
import { useApi, useMutation } from '@/hooks/useApi';
import { formatDate, parseFlexibleDate, todayISO, cn } from '@/lib/utils';
import type { FitPlan, PlanType } from '@/lib/types';

const PLAN_ICONS: Record<PlanType, typeof Calendar> = {
  vacation: Plane,
  business: Briefcase,
  event: PartyPopper,
  other: Calendar,
};

function formatRange(start: string, end: string): string {
  const startStr = parseFlexibleDate(start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const endStr = parseFlexibleDate(end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${startStr} – ${endStr}`;
}

function startsIn(start: string): string {
  const today = parseFlexibleDate(todayISO());
  const target = parseFlexibleDate(start);
  const diff = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diff === 0) return 'Starts today';
  if (diff === 1) return 'Starts tomorrow';
  if (diff < 0) return 'Past';
  return `Starts in ${diff} days`;
}

export function PlannerPage() {
  const { data, loading, error, refetch } = useApi<{ plans: FitPlan[] }>('/plans');
  const { mutate, loading: deleting } = useMutation();
  const [deleteTarget, setDeleteTarget] = useState<FitPlan | null>(null);

  const today = todayISO();
  const plans = data?.plans ?? [];
  const upcoming = plans
    .filter((p) => p.startDate >= today)
    .sort((a, b) => a.startDate.localeCompare(b.startDate));
  const past = plans
    .filter((p) => p.startDate < today)
    .sort((a, b) => b.startDate.localeCompare(a.startDate));

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await mutate('delete', `/plans/${deleteTarget.id}`);
    setDeleteTarget(null);
    refetch();
  };

  return (
    <Page>
      <PageHeader section="Fit Planner" backHref="/fits" backLabel="Fits" />
      <Main className="space-y-5">
        <Link to="/fits/planner/new">
          <Button fullWidth>
            <Plus className="w-5 h-5" />
            Create New Plan
          </Button>
        </Link>

        {loading && <LoadingState />}
        {error && <ErrorState message={error} />}

        {!loading && !error && plans.length === 0 && (
          <EmptyState
            icon={<Calendar className="w-8 h-8" />}
            title="No plans yet"
            description="Create your first outfit plan for an upcoming trip or event."
          />
        )}

        {upcoming.length > 0 && (
          <section className="space-y-3">
            <h2 className="text-sm font-semibold text-foreground">Upcoming Plans</h2>
            {upcoming.map((plan) => (
              <PlanCard key={plan.id} plan={plan} onDelete={() => setDeleteTarget(plan)} />
            ))}
          </section>
        )}

        {past.length > 0 && (
          <section className="space-y-3">
            <h2 className="text-sm font-semibold text-muted-foreground">Past Plans</h2>
            {past.map((plan) => (
              <PlanCard key={plan.id} plan={plan} past />
            ))}
          </section>
        )}
      </Main>

      <Modal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title="Delete Plan?"
        footer={
          <div className="flex gap-3">
            <Button variant="outline" fullWidth onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" fullWidth onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-muted-foreground">
          This will permanently delete
          {deleteTarget ? ` “${deleteTarget.name}”` : ' this plan'} and all its scheduled fits. This action cannot be
          undone.
        </p>
      </Modal>
    </Page>
  );
}

function PlanCard({
  plan,
  past = false,
  onDelete,
}: {
  plan: FitPlan;
  past?: boolean;
  onDelete?: () => void;
}) {
  const navigate = useNavigate();
  const Icon = PLAN_ICONS[plan.planType] ?? Calendar;
  const fitCount = plan.fitCount ?? 0;
  const warningCount = plan.warningCount ?? 0;

  return (
    <Card className={cn('overflow-hidden', past && 'opacity-60')}>
      <Link to={`/fits/planner/${plan.id}`} className="flex items-center gap-4 p-4 hover:bg-secondary/30 transition-colors">
        <div className="p-3 bg-secondary rounded-xl">
          <Icon className="w-5 h-5 text-muted-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-foreground truncate">{plan.name}</h3>
            {warningCount > 0 && (
              <Badge tone="warning" className="flex-shrink-0">
                {warningCount} warning{warningCount > 1 ? 's' : ''}
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">{formatRange(plan.startDate, plan.endDate)}</p>
          <div className="flex items-center gap-3 mt-1">
            <span className="text-xs text-muted-foreground">
              {fitCount} fit{fitCount !== 1 ? 's' : ''} planned
            </span>
            {!past && <span className="text-xs font-medium text-primary">{startsIn(plan.startDate)}</span>}
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
      </Link>

      {!past && onDelete && (
        <div className="flex items-center border-t border-border">
          <button
            onClick={() => navigate(`/fits/planner/${plan.id}`)}
            className="flex-1 py-2.5 text-xs font-medium text-muted-foreground hover:text-primary hover:bg-secondary/50 transition-colors text-center"
          >
            Edit
          </button>
          <div className="w-px h-6 bg-border" />
          <button
            onClick={onDelete}
            className="flex-1 py-2.5 inline-flex items-center justify-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-destructive hover:bg-destructive/5 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete
          </button>
        </div>
      )}

      {past && (
        <div className="px-4 pb-3 -mt-2">
          <p className="text-[11px] text-muted-foreground">{formatDate(plan.endDate)} ended</p>
        </div>
      )}
    </Card>
  );
}
